import React, { useState } from 'react';
import { Box, Stack, Typography, Paper, Chip, IconButton, Tooltip, CircularProgress } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Section, COLORS, formatDate, formatFileSize } from '../ViewPatientDialog.utils';

function DocumentCard({ document, apiUrl, token, onRequestDelete, isDeleting }) {
  const [downloading, setDownloading] = useState(false);

  const downloadDocument = async () => {
    if (!document?.id_patient_document) {
      toast.error('Fichier introuvable');
      return;
    }
    try {
      setDownloading(true);
      const res = await axios.get(`${apiUrl}/admin/patients/documents/${document.id_patient_document}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = window.document.createElement('a');
      link.href = blobUrl;
      link.download = document.nom_original || 'document';
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Erreur téléchargement document:', err);
      toast.error('Erreur lors du téléchargement du document');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: '12px', border: `1px solid ${COLORS.mutedBorder}`, backgroundColor: '#FAFBFD' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
        <Box sx={{ width: 46, height: 46, borderRadius: '10px', backgroundColor: COLORS.primarySoft, display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.primary, flexShrink: 0, overflow: 'hidden' }}>
          <AttachFileIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: COLORS.text, fontWeight: 700, wordBreak: 'break-word' }}>{document.nom_original || 'Document'}</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
            <Chip size="small" label={document.type_mime || 'Type inconnu'} sx={{ fontSize: '11px', height: 23, backgroundColor: '#F4F7FE', color: COLORS.primary }} />
            <Chip size="small" label={formatFileSize(document.taille)} sx={{ fontSize: '11px', height: 23, backgroundColor: '#F8FAFC', color: '#667085' }} />
            <Chip size="small" label={formatDate(document.created_at)} sx={{ fontSize: '11px', height: 23, backgroundColor: '#F8FAFC', color: '#667085' }} />
          </Stack>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Télécharger le document">
            <span>
              <IconButton onClick={downloadDocument} disabled={downloading} sx={{ color: COLORS.primary, backgroundColor: COLORS.primarySoft, '&:hover': { backgroundColor: '#E5E0FF' } }}>
                {downloading ? <CircularProgress size={20} thickness={5} /> : <DownloadIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Supprimer le document">
            <span>
              <IconButton onClick={() => onRequestDelete?.(document)} disabled={isDeleting} sx={{ color: '#E23D3D', backgroundColor: '#FDEDED', '&:hover': { backgroundColor: '#FBDCDC' } }}>
                {isDeleting ? <CircularProgress size={20} thickness={5} /> : <DeleteOutlineIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
}

export default function DocumentsTab({ documents, apiUrl, token, onRequestDelete, deletingDocId }) {
  const documentsCount = documents.length;

  return (
    <Section icon={<AttachFileIcon />} title="Documents du dossier" subtitle={`${documentsCount} document(s) associé(s)`}>
      {documentsCount > 0 ? (
        <Stack spacing={1.5}>
          {documents.map((document) => (
            <DocumentCard
              key={document.id_patient_document}
              document={document}
              apiUrl={apiUrl}
              token={token}
              onRequestDelete={onRequestDelete}
              isDeleting={deletingDocId === document.id_patient_document}
            />
          ))}
        </Stack>
      ) : (
        <Box sx={{ py: 4, textAlign: 'center', borderRadius: '12px', backgroundColor: COLORS.bg, border: '1px dashed #D8DEE9' }}>
          <AttachFileIcon sx={{ color: '#A3AED0', fontSize: 35, mb: 1 }} />
          <Typography sx={{ color: COLORS.muted, fontWeight: 600 }}>Aucun document associé à ce dossier.</Typography>
        </Box>
      )}
    </Section>
  );
}